import { createContext, useContext, useEffect, useState, useCallback } from "react";
import * as authService from "../services/authService";
import { TOKEN_KEY, USER_KEY, UNAUTHORIZED_EVENT } from "../api/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true); // true only during the initial session-restore check
  const [error, setError] = useState("");

  const persist = (token, userData) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    setUser(userData);
  };

  const clear = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  };

  // Session restore on load — re-validates against the backend rather
  // than trusting the localStorage snapshot forever (mirrors what
  // /auth/validate's own docstring says the frontend should do).
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setBooting(false);
      return;
    }
    authService
      .validate()
      .then((data) => {
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        setUser(data.user);
      })
      .catch(() => clear())
      .finally(() => setBooting(false));
  }, []);

  // A 401 from any request means the session is gone: drop the in-memory
  // user too so the UI stops showing a logged-in state.
  useEffect(() => {
    const onUnauthorized = () => setUser(null);
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const login = useCallback(async (identifier, password) => {
    setError("");
    try {
      const data = await authService.login({ identifier, password });
      persist(data.access_token, data.user);
      return data.user;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid email or password.";
      setError(typeof msg === "string" ? msg : "Invalid email or password.");
      throw err;
    }
  }, []);

  const register = useCallback(async (payload) => {
    setError("");
    try {
      return await authService.register(payload);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Unable to register.";
      setError(typeof msg === "string" ? msg : "Unable to register.");
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // best-effort — clear locally regardless
    }
    clear();
  }, []);

  const updateProfile = useCallback(async (payload) => {
    const data = await authService.updateProfile(payload);
    persist(data.access_token, data.user); // token is reissued on email change — must re-store
    return data.user;
  }, []);

  // Same canonical PUT /auth/me/photo call Admin's own self-service photo
  // edit uses — deliberately separate from updateProfile above since this
  // endpoint reissues no token (email hasn't changed) and returns only
  // {photo}, not a full user. Merges into the existing stored user object
  // instead of replacing it, so name/email/role/etc are never clobbered by
  // a photo-only save, and persists to the same USER_KEY snapshot
  // AuthContext's own session-restore reads on next load.
  const updatePhoto = useCallback(async (photo) => {
    const data = await authService.updatePhoto(photo);
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, photo: data.photo };
      localStorage.setItem(USER_KEY, JSON.stringify(next));
      return next;
    });
    return data.photo;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: Boolean(user),
        booting,
        error,
        login,
        register,
        logout,
        updateProfile,
        updatePhoto,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
